import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { obtenerMenusBloqueados } from '@/hooks/useMenusBloqueados';
import { useAuth } from '@/contexts/AuthContext';
import { esUsuarioA } from '@/lib/access/usuarioA';

interface AllowedMenu {
  path: string;
  canRead: boolean;
}

/**
 * ¿La ruta cae bajo un submenú que tiene la propiedad consultada (apagado,
 * reservado a Usuario A)? Decide el submenú REGISTRADO más específico que la
 * cubre —match exacto o prefijo con frontera "/"—, de modo que las subrutas
 * hereden el estado del padre (`/admin/usuarios` apagado también bloquea
 * `/admin/usuarios/nuevo`) sin que un padre marcado tumbe a un hijo que tiene
 * su propio submenú sin la marca.
 *
 * `marcadas` son las rutas con la propiedad; `neutras`, las servidas por algún
 * submenú activo. Una ruta presente en ambas cuenta como marcada (los llamadores
 * ya resuelven el empate: la marca solo queda si ningún submenú abierto la sirve).
 */
function resolverPorSubmenuMasEspecifico(
  path: string,
  marcadas: Set<string>,
  neutras: Set<string>,
): boolean {
  const clean = path.replace(/\/+$/, '') || path;
  let bestLength = -1;
  let bestMarcada = false;
  const consider = (known: string, marcada: boolean) => {
    if (!known) return;
    // Una ruta en ambos sets ya se decidió como marcada: no dejar que la copia
    // neutra, con la misma longitud, gane el empate según el orden de iteración.
    if (!marcada && marcadas.has(known)) return;
    const isExact = clean === known;
    // `/admin` (submenú Dashboard) es prefijo de TODA la app: nunca debe
    // heredar su estado a las demás rutas, solo aplicar al match exacto.
    if (!isExact && (known === '/admin' || !clean.startsWith(`${known}/`))) return;
    if (known.length > bestLength) {
      bestLength = known.length;
      bestMarcada = marcada;
    }
  };
  neutras.forEach((p) => consider(p, false));
  marcadas.forEach((p) => consider(p, true));
  return bestMarcada;
}

export function useAllowedMenus() {
  const { profile, isLoading: isAuthLoading, user, permissionVersion } = useAuth();
  const [allowedPaths, setAllowedPaths] = useState<Set<string>>(new Set());
  const [disabledPaths, setDisabledPaths] = useState<Set<string>>(new Set());
  const [enabledPaths, setEnabledPaths] = useState<Set<string>>(new Set());
  // Rutas de submenús marcados `solo_usuarioa`: reservadas a Usuario A. Se
  // cargan junto al catálogo de rutas apagadas porque son el mismo tipo de
  // gate (propiedad de la ruta, no del rol) y deben estar antes de autorizar.
  const [soloUsuarioAPaths, setSoloUsuarioAPaths] = useState<Set<string>>(new Set());
  // El catálogo de rutas apagadas se carga aparte de los permisos del rol y es
  // un gate de seguridad: hasta que llegue no se puede decidir si una vista está
  // apagada, así que forma parte del estado de carga (antes no lo era y la vista
  // se alcanzaba a renderizar antes del redirect).
  const [isLoadingDisabled, setIsLoadingDisabled] = useState(true);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Ref para evitar mostrar spinner en recargas subsecuentes
  const hasLoadedOnce = useRef(false);
  // Identidad (correo) del perfil con el que se cargaron los permisos actuales.
  // Sirve para distinguir una recarga silenciosa del MISMO usuario de un cambio
  // de cuenta: en el segundo caso los permisos viejos no pueden seguir usándose
  // como base de autorización mientras llegan los nuevos.
  const identidadCargadaRef = useRef<string | null>(null);

  // OJO: este hook resuelve AUTORIZACIÓN de rutas (lo consume `PermissionRoute`),
  // no solo el pintado del menú. Por eso usa SIEMPRE el rol de la sesión real y
  // nunca el del impersonado: con el rol ajeno, un admin en "Vista del usuario"
  // perdía el acceso a la ruta, caía en `/admin/access-denied` —que vive fuera
  // del layout del portal— y se quedaba sin forma de dejar de impersonar.
  // La simulación de la vista fiel se hace en los hooks de menú de cada portal.
  const isSuperAdmin = profile?.rol_nombre === 'Super Administrador';
  const effectiveRolId = profile?.rol_id ?? null;
  
  // Profile is still loading if we have a user but no profile yet
  const isProfileStillLoading = !!user && !profile && !isAuthLoading;

  // Rutas cuyo submenú está apagado (submenus.activo=false) o cuyo menú padre
  // está apagado (menus.activo=false). Un submenú apagado NO debe mostrarse a
  // ningún rol — incluido Super Admin, cuyo wildcard antes lo ignoraba.
  const fetchDisabledPaths = useCallback(async () => {
    try {
      const { data } = await supabase
        .from('submenus')
        .select('vista_front_end, activo, solo_usuarioa, menus!inner(activo)')
        .range(0, 4999);
      // Una misma ruta puede tener VARIOS submenús (p. ej. duplicados en un menú
      // viejo desactivado). La ruta solo debe apagarse si NINGÚN submenú activo
      // (con menú activo) la provee; si al menos uno la sirve, se muestra —incluido
      // Super Admin—. Antes bastaba un duplicado apagado para ocultar la ruta real.
      const enabled = new Set<string>();
      const disabled = new Set<string>();
      // Misma lógica de "al menos uno la sirve" para la restricción de Usuario A:
      // la ruta queda reservada solo si TODOS los submenús activos que la
      // proveen están marcados `solo_usuarioa`. Si un submenú abierto la sirve,
      // la ruta es de todos y restringirla sería negar un acceso legítimo.
      const soloA = new Set<string>();
      const abiertas = new Set<string>();
      (data ?? []).forEach((s: any) => {
        if (!s.vista_front_end) return;
        const menuActivo = s.menus?.activo !== false;
        if (s.activo !== false && menuActivo) {
          enabled.add(s.vista_front_end);
          if (s.solo_usuarioa === true) {
            soloA.add(s.vista_front_end);
          } else {
            abiertas.add(s.vista_front_end);
          }
        } else {
          disabled.add(s.vista_front_end);
        }
      });
      enabled.forEach((p) => disabled.delete(p));
      abiertas.forEach((p) => soloA.delete(p));
      setDisabledPaths(disabled);
      setEnabledPaths(enabled);
      setSoloUsuarioAPaths(soloA);
    } catch (err) {
      // Fail-open: si no se pudo cargar, no ocultar nada extra.
      console.error('Error fetching disabled submenu paths:', err);
    } finally {
      setIsLoadingDisabled(false);
    }
  }, []);

  const fetchAllowedMenus = useCallback(async () => {
    if (!effectiveRolId) return;
    
    try {
      // Solo mostrar spinner la primera vez, recargas son silenciosas
      if (!hasLoadedOnce.current) {
        setIsLoadingPermissions(true);
      }
      setError(null);
      // Get all submenus where user has 'leer' permission
      // First get the 'leer' permission id
      const { data: permisoData } = await supabase
        .from('permisos')
        .select('id')
        .eq('nombre', 'leer')
        .single();

      if (!permisoData) {
        setAllowedPaths(new Set());
        return;
      }

      // Get submenus_permisos for this role and permission
      const { data: permisosData, error: permisosError } = await supabase
        .from('submenus_permisos')
        .select('submenu_id')
        .eq('rol_id', effectiveRolId)
        .eq('permiso_id', permisoData.id)
        .eq('activo', true);

      if (permisosError) {
        console.error('Error fetching permissions:', permisosError);
        setError(permisosError.message || 'No se pudieron cargar los permisos');
        setAllowedPaths(new Set());
        return;
      }

      // Get the submenu paths
      const submenuIds = permisosData?.map(p => p.submenu_id) || [];
      
      if (submenuIds.length === 0) {
        setAllowedPaths(new Set());
        return;
      }

      // Solo submenús activos cuyo menú padre también está activo: un menú
      // apagado apaga todas sus vistas aunque el submenú siga activo=true.
      const { data: submenusData, error: submenusError } = await supabase
        .from('submenus')
        .select('vista_front_end, menu_id, menus!inner(activo)')
        .in('id', submenuIds)
        .eq('activo', true)
        .eq('menus.activo', true);

      // Portales individualizados donde esta sesión no está en la lista. Aquí
      // importa tanto como en el sidebar: este hook es el que consume
      // PermissionRoute, así que sin esto el portal se escondería del menú pero
      // la ruta seguiría abierta a quien la escribiera a mano.
      const bloqueados = await obtenerMenusBloqueados(permissionVersion);

      if (submenusError) {
        console.error('Error fetching submenus:', submenusError);
        setError(submenusError.message || 'No se pudieron cargar los submenús');
        setAllowedPaths(new Set());
        return;
      }

      const paths = new Set<string>();
      submenusData?.forEach((item: any) => {
        if (item.vista_front_end && !bloqueados.has(Number(item.menu_id))) {
          paths.add(item.vista_front_end);
        }
      });

      setAllowedPaths(paths);
      hasLoadedOnce.current = true;
    } catch (err) {
      console.error('Error in fetchAllowedMenus:', err);
      setError((err as Error)?.message || 'Error inesperado al cargar permisos');
      // Solo limpiar paths si nunca hemos cargado exitosamente
      if (!hasLoadedOnce.current) {
        setAllowedPaths(new Set());
      }
    } finally {
      setIsLoadingPermissions(false);
    }
  }, [effectiveRolId, permissionVersion]);

  useEffect(() => {
    // Wait for auth to finish loading
    if (isAuthLoading) {
      return;
    }

    // If we have a user but profile hasn't loaded yet, wait
    if (user && !profile) {
      return;
    }

    // Cambió la cuenta: descartar los permisos del usuario anterior y volver a
    // estado de carga. Sin esto, `hasLoadedOnce` mantenía el spinner apagado y
    // PermissionRoute autorizaba (o negaba) con el rol del usuario que se fue.
    const identidad = profile?.email ?? null;
    if (identidadCargadaRef.current !== identidad) {
      identidadCargadaRef.current = identidad;
      hasLoadedOnce.current = false;
      setAllowedPaths(new Set());
      setIsLoadingPermissions(true);
    }

    // If Super Admin, skip fetching permissions — but still load the disabled
    // paths so toggled-off submenus/menus stay hidden for everyone.
    if (isSuperAdmin) {
      setAllowedPaths(new Set(['*']));
      setIsLoadingPermissions(false);
      setError(null);
      fetchDisabledPaths();
      return;
    }

    // If no profile (not logged in), stop loading
    if (!effectiveRolId) {
      setIsLoadingPermissions(false);
      setIsLoadingDisabled(false);
      return;
    }

    fetchAllowedMenus();
    fetchDisabledPaths();
  }, [effectiveRolId, isSuperAdmin, isAuthLoading, user, profile, permissionVersion, fetchAllowedMenus, fetchDisabledPaths]);

  // ¿La ruta está apagada en BD? Se resuelve con el submenú REGISTRADO más
  // específico que cubra la ruta (match exacto o prefijo con frontera "/"), para
  // que las subrutas hereden el apagado del padre (ej. /admin/usuarios apagado
  // también bloquea /admin/usuarios/nuevo) sin que un padre apagado tumbe a un
  // hijo que sí tiene su propio submenú activo.
  const isPathDisabled = useCallback(
    (path: string): boolean => resolverPorSubmenuMasEspecifico(path, disabledPaths, enabledPaths),
    [enabledPaths, disabledPaths],
  );

  // ¿La ruta está reservada a Usuario A (`submenus.solo_usuarioa`)? Se resuelve
  // igual que el apagado —por el submenú registrado más específico— para que las
  // subrutas hereden la reserva del padre: sin esto, `/admin/administrar-menus`
  // quedaba cerrado pero cualquier ruta hija suya seguía abierta.
  const isPathSoloUsuarioA = useCallback(
    (path: string): boolean =>
      resolverPorSubmenuMasEspecifico(path, soloUsuarioAPaths, enabledPaths),
    [soloUsuarioAPaths, enabledPaths],
  );

  // El dueño de las vistas restringidas pasa; cualquier otra cuenta —incluidos
  // los demás Super Admin, que autorizan con el wildcard '*'— no.
  const puedeVerSoloUsuarioA = esUsuarioA(profile?.email);

  const isPathRestrictedToUsuarioA = useCallback(
    (path: string): boolean => !puedeVerSoloUsuarioA && isPathSoloUsuarioA(path),
    [puedeVerSoloUsuarioA, isPathSoloUsuarioA],
  );

  const isPathAllowed = (path: string): boolean => {
    // Vistas explícitamente apagadas en BD no son accesibles para nadie.
    if (isPathDisabled(path)) return false;

    // Vistas reservadas a Usuario A: el gate va ANTES del wildcard de Super
    // Admin, que es justo el que las dejaba pasar. Un rol con `leer` en BD
    // sobre uno de esos submenús (hoy el rol 30 sobre "Administrar Menús")
    // también entraba por URL directa aunque no lo viera en el sidebar.
    if (isPathRestrictedToUsuarioA(path)) return false;

    if (isSuperAdmin || allowedPaths.has('*')) {
      return true;
    }

    // Caso especial: /admin/reportes/ver requiere acceso a cualquier submenu de reportes
    if (path === '/admin/reportes/ver' || path.startsWith('/admin/reportes/ver/')) {
      for (const allowedPath of allowedPaths) {
        if (allowedPath.includes('/reportes/') || allowedPath.includes('/configuracion-reportes')) {
          return true;
        }
      }
      return false;
    }
    
    return allowedPaths.has(path);
  };

  // Loading = auth loading OR profile still loading OR permissions loading (but not if super admin)
  // OR catálogo de rutas apagadas aún sin cargar (aplica también a Super Admin).
  const isLoading =
    isAuthLoading ||
    isProfileStillLoading ||
    isLoadingDisabled ||
    (isLoadingPermissions && !isSuperAdmin);

  return {
    isPathAllowed,
    isPathDisabled,
    /** Ruta reservada a Usuario A y la sesión actual NO es Usuario A. */
    isPathRestrictedToUsuarioA,
    /** Ruta marcada `solo_usuarioa`, sin importar quién pregunta. */
    isPathSoloUsuarioA,
    allowedPaths,
    disabledPaths,
    enabledPaths,
    soloUsuarioAPaths,
    isLoading,
    isSuperAdmin,
    error,
    refetch: fetchAllowedMenus,
  };
}
